import _ from "lodash";
import moment from "moment";

function makeTreeFromFlatList(input, indexer, maker, root = null) {
    const cache = new Map();
    // get or create (and return) children array for the passed key;
    const getChildrenArray = (key) => {
        return cache.get(key) || cache.set(key, []).get(key);
    };
    for (const item of input) {
        getChildrenArray(indexer(item)).push(maker(item, getChildrenArray));
    }
    return cache.get(root);
}
function makeFlatListFromTree(input, key) {
    return [];
}
function uniqueProps(data) {
    return _.flatMap(data, (item) => {
        return Object.keys(item);
    });
}

function shortDateString(value) {
    return moment(value).format("ddd D/M/Y");
}
function longDateString(value) {
    return moment(value).format("LLL");
}

function graphAxisDateString(value) {
    return moment(value).format("ddd, MMM D YYYY HH:mm");
}

async function getSignalTypeFromBits(value, types) {
    const flags = types?.device?.flags;

    if (!flags) throw new Error("Flags not found in types response");

    const {
        BIT_LOCATION_ANALOG,
        BIT_LOCATION_DIGITAL,
        BIT_LOCATION_INPUT,
        BIT_LOCATION_OUTPUT,
        BIT_LOCATION_STATUS,
        BIT_LOCATION_CONTROL,
        BIT_LOCATION_LO_BUS,
        BIT_LOCATION_HI_BUS,
    } = flags;

    const isAnalog = (value & (1 << BIT_LOCATION_ANALOG)) !== 0;
    const isDigital = (value & (1 << BIT_LOCATION_DIGITAL)) !== 0;
    const isInput = (value & (1 << BIT_LOCATION_INPUT)) !== 0;
    const isOutput = (value & (1 << BIT_LOCATION_OUTPUT)) !== 0;
    const isStatus = (value & (1 << BIT_LOCATION_STATUS)) !== 0;
    const isControl = (value & (1 << BIT_LOCATION_CONTROL)) !== 0;

    // Extract bus type bits
    const loBusBit = (value & (1 << BIT_LOCATION_LO_BUS)) !== 0 ? 1 : 0;
    const hiBusBit = (value & (1 << BIT_LOCATION_HI_BUS)) !== 0 ? 1 : 0;
    const busBits = (hiBusBit << 1) | loBusBit;

    console.log("=== Bus Type Debug ===");
    console.log("Value:", value, "Binary:", value.toString(2).padStart(8, "0"));
    console.log(
        "BIT_LOCATION_LO_BUS:",
        BIT_LOCATION_LO_BUS,
        "BIT_LOCATION_HI_BUS:",
        BIT_LOCATION_HI_BUS
    );
    console.log("loBusBit:", loBusBit, "hiBusBit:", hiBusBit);
    console.log("busBits:", busBits);
    console.log("=====================");

    const busFlags = {
        GPIO: busBits === 0,
        UART: busBits === 1,
        I2C: busBits === 2,
        VIRTUAL: busBits === 3,
    };

    return {
        analog: isAnalog,
        digital: isDigital,
        input: isInput,
        output: isOutput,
        control: isControl,
        status: isStatus,
        ...busFlags,
    };
}

function shortDurationString(duration) {
    const seconds = duration % 60;
    const totalMinutes = Math.floor(duration / 60);
    const minutes = totalMinutes % 60;
    const totalHours = Math.floor(totalMinutes / 60);
    const hours = totalHours % 24;
    const totalDays = Math.floor(totalHours / 24);
    const days = totalDays % 7;
    const weeks = Math.floor(totalDays / 7) % 4; // Approximate 4 weeks per month
    const months = Math.floor(totalDays / 30) % 12; // Approximate 30 days per month
    const years = Math.floor(totalDays / 365);

    if (duration < 60) {
        return `${seconds}s`; // Less than a minute
    } else if (duration < 3600) {
        return `${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(
            2,
            "0"
        )}s`; // Less than an hour
    } else if (duration < 86400) {
        return `${String(hours).padStart(2, "0")}h ${String(minutes).padStart(
            2,
            "0"
        )}m ${String(seconds).padStart(2, "0")}s`; // Less than a day
    } else if (duration < 604800) {
        return `${totalDays}d ${String(hours).padStart(2, "0")}h ${String(
            minutes
        ).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`; // Less than a week
    } else if (duration < 2592000) {
        return `${weeks}w ${days}d ${String(hours).padStart(2, "0")}h ${String(
            minutes
        ).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`; // Less than a month
    } else if (duration < 31536000) {
        return `${months}mo ${weeks}w ${days}d ${String(hours).padStart(
            2,
            "0"
        )}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(
            2,
            "0"
        )}s`; // Less than a year
    } else {
        return `${years}y ${months}mo ${weeks}w ${days}d ${String(
            hours
        ).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m ${String(
            seconds
        ).padStart(2, "0")}s`; // More than a year
    }
}

function timeString(value) {
    return moment(value * 1000).format("HH:mm");
}
function msFromUnix(value) {
    return value * 1000;
}
function unixFromMs(value) {
    return value / 1000;
}
function clampedText(value, min, max, minString, maxString) {
    if (value === max) return maxString;
    if (value === min) return minString;
    return value;
}

function generateSubIntervals(
    startTime,
    endTime,
    splitInterval = "minute",
    quantum = 1
) {
    const timeSeries = [];
    while (startTime.isSameOrBefore(endTime)) {
        timeSeries.push(startTime.add(quantum, splitInterval).clone());
    }
    return timeSeries;
}

function getPageSizes() {
    return [5, 10, 20];
}

function getPriorityColor(timeInSeconds) {
    const days = Math.floor(timeInSeconds / 86400);
    if (days < 1) return "tw-text-red-500 tw-bg-red-100";
    if (days < 7) return "tw-text-yellow-500 tw-bg-yellow-100";
    return "tw-text-green-500 tw-bg-green-100";
}

function formatTimeToNextMaintenance(timeInSeconds) {
    const days = Math.floor(timeInSeconds / 86400);
    const hours = Math.floor((timeInSeconds % 86400) / 3600);
    return `${days} days ${hours} hours to go`;
}

function getHeartbeatStatus(epoch) {
    const lastSeen = moment(epoch);
    const now = moment();
    const diffInSeconds = now.diff(lastSeen, "seconds");

    let label;
    let style;

    if (diffInSeconds < 60) {
        label = `${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""} ago`;
        style = "tw-text-green-500 tw-bg-green-100";
    } else if (diffInSeconds < 3600) {
        const diffInMinutes = now.diff(lastSeen, "minutes");
        label = `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
        style =
            diffInSeconds < 300
                ? "tw-text-yellow-500 tw-bg-yellow-100"
                : "tw-text-red-500 tw-bg-red-100";
    } else if (diffInSeconds < 86400) {
        const diffInHours = now.diff(lastSeen, "hours");
        label = `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
        style = "tw-text-red-500 tw-bg-red-100";
    } else {
        const diffInDays = now.diff(lastSeen, "days");
        label = `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
        style = "tw-text-red-500 tw-bg-red-100";
    }

    return { label, style };
}

function printReports(element) {
    if (!element) return;

    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (!printWindow) {
        alert("Please allow popups to print");
        return;
    }

    const doc = printWindow.document;
    doc.open();
    doc.write("<html><head><title>Print Report</title>");

    const styles = document.querySelectorAll('style, link[rel="stylesheet"]');
    styles.forEach((style) => {
        doc.write(style.outerHTML);
    });

    doc.write(
        "<style>body { margin: 0; padding: 20px; background-color: white; } .tw-h-full { height: auto !important; } .tw-overflow-y-auto { overflow: visible !important; height: auto !important; }</style>"
    );

    doc.write("</head><body>");
    doc.write(element.outerHTML);
    doc.write("</body></html>");
    doc.close();

    printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
    };

    setTimeout(() => {
        printWindow.focus();
        printWindow.print();
    }, 500);
}

export {
    makeFlatListFromTree,
    makeTreeFromFlatList,
    uniqueProps,
    shortDateString,
    longDateString,
    graphAxisDateString,
    shortDurationString,
    timeString,
    msFromUnix,
    unixFromMs,
    clampedText,
    generateSubIntervals,
    getPageSizes,
    getSignalTypeFromBits,
    getGpioLabelOptionsFromType,
    getPriorityColor,
    formatTimeToNextMaintenance,
    getHeartbeatStatus,
    printReports,
};

// Helper function to get GPIO label options from type bitmap without API dependency
function getGpioLabelOptionsFromType(channelType) {
    // Bit flags based on SIGNAL_TYPE_BITMAP
    // D0: O (Output), D1: I (Input), D2: D (Discrete), D3: A (Analog), D5: C (Control)
    const isAnalog = (channelType & (1 << 3)) !== 0; // Bit 3: A (Analog)
    const isDiscrete = (channelType & (1 << 2)) !== 0; // Bit 2: D (Discrete)
    const isInput = (channelType & (1 << 1)) !== 0; // Bit 1: I (Input) -> Sense
    const isOutput = (channelType & (1 << 0)) !== 0; // Bit 0: O (Output) -> Actuate
    const isControl = (channelType & (1 << 5)) !== 0; // Bit 5: C (Control)

    // Always provide all options, but determine which should be selected based on type
    const options1 = ["Discrete", "Proportional"];
    const selectedLabel1 = isAnalog ? "Proportional" : "Discrete";

    // Determine Label 2 options - always provide both Sense and Actuate
    const options2 = ["Sense", "Actuate"];
    const selectedLabel2 = isOutput ? "Actuate" : "Sense";

    // Determine Label 3 options (Control vs State)
    const options3 = ["Control", "State"];
    const selectedLabel3 = isControl ? "Control" : "State";

    return {
        options1,
        selectedLabel1,
        options2,
        selectedLabel2,
        options3,
        selectedLabel3,
    };
}
